import { computed, inject, Injectable, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthFacade, AuthorizationConcept, AuthorizationService, AuthorizationType } from '@iot-platform/auth';
import { GridEngineFacade } from '@iot-platform/grid-engine';
import { BaseFacade, BusinessProfile, Filter, Pagination, UserAccount } from '@iot-platform/models/common';
import { Store } from '@ngrx/store';
import { noop } from 'rxjs';
import { UsersActions } from '../actions/users.actions';
import { UsersSelectors } from '../selectors/users.selectors';

/* eslint-disable @angular-eslint/prefer-inject */
@Injectable({
  providedIn: 'root'
})
export class UsersFacade extends BaseFacade<UserAccount, Pagination, Filter> {
  protected readonly gridEngineFacade: GridEngineFacade = inject(GridEngineFacade);
  businessProfilesByUser: Signal<BusinessProfile[]> = this.store.selectSignal(this.selector.selectBusinessProfilesByUser);
  canCreateUser: Signal<boolean> = computed(() => {
    this.authFacade.privileges();
    return this.authorizationService.applyAuthorization(AuthorizationConcept.USER, AuthorizationType.CREATE);
  });
  canUpdateUser: Signal<boolean> = computed(() => {
    this.authFacade.privileges();
    return this.authorizationService.applyAuthorization(AuthorizationConcept.USER, AuthorizationType.UPDATE);
  });
  canUpdateUserStatus: Signal<boolean> = computed(() => {
    this.authFacade.privileges();
    return this.authorizationService.applyAuthorization(AuthorizationConcept.USER_STATUS, AuthorizationType.UPDATE);
  });
  canReadHistory: Signal<boolean> = computed(() => {
    this.authFacade.privileges();
    return this.authorizationService.applyAuthorization(AuthorizationConcept.AUDIT_TRAIL, AuthorizationType.READ);
  });
  grids = this.gridEngineFacade.usersGrids;
  gridConfiguration = this.gridEngineFacade.usersGridsConfiguration;
  grids$ = toObservable(this.grids);
  gridConfiguration$ = toObservable(this.gridConfiguration);

  constructor(
    protected override store: Store,
    protected override selector: UsersSelectors,
    protected readonly authFacade: AuthFacade,
    protected readonly authorizationService: AuthorizationService
  ) {
    super(store, selector);
  }

  getAll(): void {
    noop();
  }

  setFilters(filters: Filter[]): void {
    this.store.dispatch(UsersActions.setFilters({ filters }));
  }

  getUserById(userId: string): void {
    this.store.dispatch(UsersActions.getUserById({ userId }));
  }

  addUser(user: UserAccount): void {
    this.store.dispatch(UsersActions.addUser({ user }));
  }

  updateUser(user: UserAccount): void {
    this.store.dispatch(UsersActions.updateUser({ user }));
  }

  enableUser(user: UserAccount): void {
    this.store.dispatch(UsersActions.enableUser({ user }));
  }

  disableUser(user: UserAccount): void {
    this.store.dispatch(UsersActions.disableUser({ user }));
  }

  resendUserValidationLink(user: UserAccount): void {
    this.store.dispatch(UsersActions.resendUserValidationLink({ user }));
  }

  navigateToUserDetails(userId: string): void {
    this.store.dispatch(UsersActions.navigateToUserDetails({ userId }));
  }

  getBusinessProfilesByUserId(userId: string): void {
    this.store.dispatch(UsersActions.getBusinessProfilesByUserId({ userId }));
  }

  addUserToCurrentGrid(item: UserAccount): void {
    const gridId = this.gridConfiguration()?.currentGrid?.id;
    if (gridId) {
      this.gridEngineFacade.addItemInGridData(gridId, item);
    }
  }

  updateUserInCurrentGrid(item: UserAccount): void {
    const gridId = this.gridConfiguration()?.currentGrid?.id;
    if (gridId) {
      this.gridEngineFacade.updateItemInGridData(gridId, item);
    }
  }
}
